// pages/EpicCallbackPage.js
//
// Route: /epic-callback on app.nephrotrax.com (or localhost during dev)
// Epic redirects the patient's browser here after they authorize access.
//
// IMPORTANT #1: This is a full top-level page reload — the whole React app
// remounts, including Auth0Provider. Even with cacheLocation="localstorage",
// Auth0 needs a moment to asynchronously rehydrate the session before
// isAuthenticated flips true. This component waits for that instead of
// racing ahead of it.
//
// IMPORTANT #2: code/state are NOT read from window.location here. An
// inline script in public/index.html strips them from the URL and stashes
// them in sessionStorage BEFORE React ever mounts — this prevents
// Auth0Provider (which scans window.location.search on every load for its
// own code/state) from mistakenly intercepting Epic's OAuth callback and
// corrupting the Auth0 session as a side effect. See
// FIX_auth0_epic_collision.txt for the full explanation.

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '../hooks/useAuth0';
import { useEpicConnect } from '../hooks/useEpicConnect';
import { initializeApiWithAuth0 } from '../services/api';

const EpicCallbackPage = () => {
  const navigate = useNavigate();
  const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const { handleEpicCallback } = useEpicConnect();
  const [status, setStatus] = useState('waiting'); // 'waiting' | 'connecting' | 'success' | 'error'
  const hasRun = useRef(false);

  useEffect(() => {
    if (isLoading) {
      setStatus('waiting');
      return;
    }

    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }

    if (hasRun.current) return;
    hasRun.current = true;

    (async () => {
      setStatus('connecting');

      initializeApiWithAuth0(getAccessTokenSilently);

      // Read code/state from where the inline index.html script stashed
      // them, NOT from window.location — the URL has already been
      // stripped clean at this point.
      const code = sessionStorage.getItem('epic_pending_code');
      const state = sessionStorage.getItem('epic_pending_state');
      sessionStorage.removeItem('epic_pending_code');
      sessionStorage.removeItem('epic_pending_state');

      if (!code || !state) {
        console.error('Epic callback: no pending code/state found in sessionStorage');
        setStatus('error');
        setTimeout(() => navigate('/dashboard', { replace: true }), 3000);
        return;
      }

      // Reconstruct a URL-shaped string so handleEpicCallback's existing
      // URLSearchParams parsing logic doesn't need to change.
      const reconstructedUrl =
        `${window.location.origin}${window.location.pathname}?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`;

      const success = await handleEpicCallback(reconstructedUrl);
      setStatus(success ? 'success' : 'error');
      setTimeout(() => navigate('/dashboard', { replace: true }), success ? 1200 : 3000);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isAuthenticated]);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 16,
        fontFamily: "'DM Sans', system-ui, sans-serif",
        background: 'linear-gradient(135deg, #0a1628 0%, #0D2B45 40%, #2d1b69 100%)',
        color: 'white',
        padding: 24,
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: 40 }}>
        {(status === 'waiting' || status === 'connecting') && '🔄'}
        {status === 'success' && '✅'}
        {status === 'error' && '⚠️'}
      </div>
      <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.85)' }}>
        {status === 'waiting' && 'Restoring your session…'}
        {status === 'connecting' && 'Connecting your health records…'}
        {status === 'success' && 'Connected! Taking you back to your dashboard…'}
        {status === 'error' && "Something went wrong connecting your records. Redirecting you back — please try again."}
      </p>
    </div>
  );
};

export default EpicCallbackPage;